import { useState, useEffect, useContext, useRef } from 'react';

import { LiveReserveContext } from '../../../../LiveLessonReseveContext';

import {
  LiveReserveRequestPref,
  LiveReserveRequestPrefs,
} from '../../../../../../../../models/lesson/live/reserve/request/LiveReserveRequest';


/**
 * 希望日時の入力（講師リクエスト） (SPEAKOUT-1853)
 *
 * 予約の枠一覧と同じモーダルの中に置く「もう一つの日程の選び方」。
 * 選択中の講師×レッスン時間で予約できる枠が1つも無いときは自動でこちらになり、
 * 枠があるときは一覧の「他の日時をリクエストする」から切り替えて使う。
 *
 * ★入力は【このコンポーネントのローカル state】で編集し、「この日時で決定」で親(Context)へ渡す。
 *   枠を選ぶ側が「押した瞬間に確定して閉じる」のと形をそろえるため。
 * 🔴 入力欄に name を付けないこと。ここは予約フォーム <form action="/lesson/live"> の【中】に
 *   描画されるので、name を付けると通常予約の送信値に混ざる。
 */

/**
 * 時刻の刻み(分)。
 * 🔴 30分ではなく **20分**。ライブレッスンの枠(`inst_schedule` target=so_lesson・個人枠)の
 *   開始時刻は実測で **:00 / :20 / :40 が29.3万件中の99.9%**、`:30` は262件しか無い(2026-09-08)。
 *   30分刻みにすると【実在する :20 / :40 を選べず、実在しない :30 を選ばせる】ことになる。
 */
const TIME_STEP_MINUTES = 20;

/**
 * 入力欄の共通スタイル。
 * - fontSize:16px … iOS Safari は 16px 未満の入力欄をタップすると画面を勝手に拡大する
 * - 🔴 height:'auto' … 上とセットで必須。Bootstrap3 の `.form-control` は **height:34px 固定**で、
 *   16px に上げると padding(6+6)+行高が 34px を超え、**select の文字が下で見切れる**
 * - 🔴 minHeight … height:'auto' の下限。**日付欄が空のときに箱がぺしゃんこになる**のを防ぐ
 *   （下の DATE_CONTROL_STYLE 参照）。40px は同じモーダルの select の実測高さ（PC Chrome）で、
 *   「隣の開始/終了より低くならない」ことを保証する値。共通側に置いて両方へ効かせる
 */
const FORM_CONTROL_STYLE = { fontSize: '16px', height: 'auto', minHeight: '40px' };

/**
 * 日付欄(input type="date")だけの追加スタイル。
 * 🔴 iOS(Safari / iPhone の Chrome。どちらも中身は WebKit)は input[type="date"] を
 *   **内容に合わせた固有の幅**で描き、`.form-control` の `width:100%` より優先するため、
 *   親の枠から右へはみ出す（2026-09-08 実機で確認＝I-1）。
 *   appearance を外すと普通のテキスト入力と同じ箱になり width:100% が効く。
 *   `minWidth:0` は WebKit の `min-width:auto`(＝内容幅) を切るためで、これが無いと
 *   width:100% が min-width に負けて元に戻る。
 * ★**select には当てないこと**。`appearance:none` にすると【プルダウンの矢印(▼)が消え】、
 *   ただの入力欄に見える（F-1 で base-select を見送ったのと同じ理由。対象が高齢のユーザー）。
 * 🔴 appearance を外すと WebKit は date 用の内部レイアウトを持たなくなり、**値が空のとき
 *   中身の高さが 0** になる。`height:'auto'` がそれを拾って**箱がぺしゃんこに潰れる**ので、
 *   FORM_CONTROL_STYLE の `minHeight` とセットでなければ使えない（2026-09-08 実機で確認）。
 */
const DATE_CONTROL_STYLE = {
  ...FORM_CONTROL_STYLE,
  WebkitAppearance: 'none',
  appearance: 'none',
  minWidth: 0,
  maxWidth: '100%',
  boxSizing: 'border-box',
} as const;
// ★ネイティブのプルダウンは option 自身の font-size で候補を描く。
//   select 側だけ指定しても候補が既定サイズのまま小さく出る（実機確認 F-1 の指摘）
const OPTION_STYLE = { fontSize: '16px' };

/** 'H:i' を分に直す */
function toMinutes(time: string): number {
  const parts = time.split(':');
  return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
}

/** 分を 'HH:MM' にする */
function toTimeLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
}

/** Date を 'YYYY-MM-DD' にする（input type="date" の値と同じ形式） */
function toDateValue(date: Date): string {
  return date.getFullYear() + '-'
       + String(date.getMonth() + 1).padStart(2, '0') + '-'
       + String(date.getDate()).padStart(2, '0');
}


function LiveReserveDateModalFree(props: any) {

  // 空き枠の一覧へ戻れるか（枠が1つも無いときは戻り先が無いので出さない）
  const canBackToSlots = props.canBackToSlots;
  const onBackToSlots  = props.onBackToSlots;
  const setIsDispModal = props.setIsDispModal;

  const {
    lsnMin,
    requestPrefs, setRequestPrefs,
    requestMinDate, requestMaxDate,
  } = useContext(LiveReserveContext);

  // 編集中の入力。決定するまで親へは渡さない
  const [prefs, setPrefs] = useState<LiveReserveRequestPrefs>(requestPrefs);
  const [errors, setErrors] = useState<string[]>([]);
  const errorRef = useRef<HTMLDivElement>(null);

  /**
   * 希望日の下限。事務局が講師と調整して枠を作る運用なので直近は受けられない。
   * ★サーバー(app/inquiry.php)が同じ値で弾く。判定をそろえるため日付はAPIが返したものを使う。
   *   端末の時計やタイムゾーンで計算すると1日ずれ「画面は通ったのにサーバーが弾く」が起きる。
   */
  const minDate = requestMinDate || toDateValue(new Date(Date.now() + 3 * 24 * 60 * 60 * 1000));

  /**
   * 希望日の上限＝翌月末。講師のスケジュール画面が「当月1日〜翌月末」しか出さないため、
   * それより先を希望されても事務局が講師に枠を頼めず、可否の回答が遅れる。
   * ★サーバー(liveInstRequestMaxDate)が同じ値で弾く。minDate と同じくAPIの値を優先し、
   *   端末の時計でずれないようにする。予備計算も「翌月0日」＝翌月末で合わせる。
   */
  const fallbackMax = new Date();
  const maxDate = requestMaxDate
                || toDateValue(new Date(fallbackMax.getFullYear(), fallbackMax.getMonth() + 2, 0));

  // 時刻の選択肢。ライブの枠は24時間すべてに存在するので時間帯では絞らない
  const startTimes: string[] = [];
  for (let m = 0; m < 24 * 60; m += TIME_STEP_MINUTES) startTimes.push(toTimeLabel(m));
  // 終了は開始より後だけ。23:40 開始でも終わりを選べるよう 23:59 を足す
  const endTimes = startTimes.slice(1).concat(['23:59']);


  // エラーは入力欄の上に出るので、決定を押した位置（下）から見えるところまで戻してやる
  useEffect(() => {
    if (errors.length > 0 && errorRef.current) {
      errorRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [errors]);


  const setPrefValue = (index: number, key: keyof LiveReserveRequestPref, value: string) => {
    setPrefs((current) => current.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
  };

  // サーバー(validateLiveInstRequest)と同じ条件をここでも見る。
  // サーバーで弾かれると /inquiry のページへ遷移して入力が全部消えるため、ここで確実に止める
  const validate = (): string[] => {
    const messages: string[] = [];

    prefs.forEach((row, index) => {
      const n = index + 1;
      if (!row.date || !row.start || !row.end) {
        messages.push('第' + n + '希望の日付・開始/終了時刻をすべて選択してください');
        return;
      }
      if (row.date < minDate) {
        messages.push('第' + n + '希望は ' + minDate + ' 以降の日付を選択してください');
      }
      if (row.date > maxDate) {
        messages.push('第' + n + '希望は ' + maxDate + ' 以前の日付を選択してください'
                    + '（それより先の枠は講師がまだご登録できません）');
      }
      if (toMinutes(row.start) >= toMinutes(row.end)) {
        messages.push('第' + n + '希望は開始時刻より後の終了時刻を選択してください');
      }
    });

    // 第1〜第3希望が同じ日時になっていないか。
    // ★候補が実質1つしか無いと事務局が調整のしようがない。弾くのは【完全一致】だけで、
    //   「09/11 10:00〜12:00」と「09/11 11:00〜13:00」のように重なるだけの候補は通す。
    const seen: { [key: string]: number } = {};
    prefs.forEach((row, index) => {
      if (!row.date || !row.start || !row.end) return;
      const key = row.date + ' ' + row.start + '-' + row.end;
      if (seen[key]) {
        messages.push('第' + seen[key] + '希望と第' + (index + 1) + '希望が同じ日時です'
                    + '（別の候補をお選びください）');
      } else {
        seen[key] = index + 1;
      }
    });

    return messages;
  };

  /**
   * 選んだ日が受付範囲の外かどうか（外なら理由の文言、中なら空文字）。
   * validate() と同じ条件を、決定を押す前に1欄ずつ出すために使う。
   */
  const dateRangeError = (date: string): string => {
    if (!date) return '';
    if (date < minDate) return minDate + ' 以降の日付をお選びください';
    if (date > maxDate) return maxDate + ' 以前の日付をお選びください（それより先の枠は講師がまだご登録できません）';
    return '';
  };

  const onClickDecide = () => {
    const messages = validate();
    if (messages.length > 0) {
      setErrors(messages);   // 表示位置へのスクロールは errors を見る useEffect が行う
      return;
    }
    setErrors([]);
    setRequestPrefs(prefs);
    setIsDispModal(false);
  };


  return (
    // ★Modal(common/Modal.tsx) の器は透明なので、背景は中身側で持つ（枠一覧の日付カードと同じ）
    <div style={{backgroundColor: '#fff', padding: '15px', borderRadius: '8px'}}>

      <h4 className="text-center" style={{fontWeight: 'bold', marginTop: 0}}>
        ご希望の日時をお知らせください
      </h4>
      <p className="small" style={{margin: '0 0 10px 0'}}>
        ご予約枠が空いていない場合でも、ご希望をお知らせいただければ
        事務局でご希望に沿った枠のご用意を調整いたします。
        <br />
        枠をご用意するお時間をいただくため、<b>{minDate}</b> 〜 <b>{maxDate}</b> の間で
        <b>第1〜第3希望</b>をすべてお選びください。
      </p>
      <p className="small" style={{margin: '0 0 10px 0'}}>
        レッスン時間は <b>{lsnMin}分</b> でお伝えします（変更はモーダルを閉じて「レッスン時間を選択」から）。
      </p>

      {errors.length > 0 &&
        <div ref={errorRef} className="alert alert-danger" style={{padding: '10px'}}>
          {errors.map((message, index) => <div key={index}>{message}</div>)}
        </div>
      }

      {/* 🔴 <form> は置けない（外側の予約フォームと入れ子になり submit が React に届かない）。
          入力欄に name も付けない＝付けると通常予約の送信値に混ざる。 */}
      <div style={{backgroundColor: '#f5f5f5', padding: '10px', marginBottom: 10}}>
        {prefs.map((row, index) => (
          <div key={index} style={{marginBottom: index === prefs.length - 1 ? 0 : 15}}>
            <div style={{fontWeight: 'bold', fontSize: '90%'}}>第{index + 1}希望</div>
            <input
              type="date"
              className="form-control"
              style={{...DATE_CONTROL_STYLE, marginBottom: 5}}
              min={minDate}
              max={maxDate}
              value={row.date}
              onChange={(e) => setPrefValue(index, 'date', e.target.value)}
            />
            {/* 🔴 min/max は**スマホでは効かないことがある**（iOS の日付ホイールは範囲外の日も
                回して選べてしまう＝2026-09-08 実機で確認 I-4）。「この日時で決定」まで気づけないと
                3つ入れ直しになるので、選んだその場で欄の下に理由を出す。
                ★値は書き換えない（勝手に直されると何が起きたか分からないため）。 */}
            {dateRangeError(row.date) &&
              <div style={{color: '#a94442', fontSize: '90%', marginBottom: 5}}>
                {dateRangeError(row.date)}
              </div>
            }
            <div style={{display: 'flex', alignItems: 'center'}}>
              <select
                className="form-control"
                style={FORM_CONTROL_STYLE}
                value={row.start}
                onChange={(e) => setPrefValue(index, 'start', e.target.value)}
              >
                <option style={OPTION_STYLE} value="">開始</option>
                {startTimes.map((time) => <option key={time} style={OPTION_STYLE} value={time}>{time}</option>)}
              </select>
              <span style={{padding: '0 5px'}}>〜</span>
              <select
                className="form-control"
                style={FORM_CONTROL_STYLE}
                value={row.end}
                onChange={(e) => setPrefValue(index, 'end', e.target.value)}
              >
                <option style={OPTION_STYLE} value="">終了</option>
                {endTimes.map((time) => <option key={time} style={OPTION_STYLE} value={time}>{time}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>

      <div className="text-center">
        {/* ★type="button"。外側フォームの submit を誘発させないため */}
        <button
          type="button"
          className="btn btn-success btn_customize"
          style={{width: '80%', whiteSpace: 'normal', marginBottom: 10}}
          onClick={onClickDecide}
        >
          この日時で決定
        </button>

        {canBackToSlots &&
          <div>
            <button
              type="button"
              className="btn btn-link"
              style={{whiteSpace: 'normal'}}
              onClick={onBackToSlots}
            >
              ← 空き枠から選ぶ
            </button>
          </div>
        }
      </div>

      <div className="small" style={{marginTop: '10px'}}>
        ※ この内容は事務局へのリクエストです。<b>この時点ではご予約は確定しません。</b><br />
        ※ 希望講師の都合により、ご予約が出来ない場合がございます。予めご了承ください。<br />
        ※ 希望時間に応じて、予約チケット枚数が変化します。（15分3チケット、30分6チケット、45分9チケット）
      </div>

    </div>
  );
}

export default LiveReserveDateModalFree;
